import { INDEX_LENGTH_U32 } from "plugins/ColorFinder/processing/SuperIndexController.class"
import { ColorFinderTiler } from "../ColorFinderTiler.class"

// Base color (BGR format: Blue=66, Green=FF, Red=F1)
const ANOMALY_COLOR_BGR = 0x0066fff1

// Higher value = Stricter visuals. Weak anomalies fade out much faster.
const OPACITY_GAMMA = 3.0

const createAnomalyMask = (anomalyFilter: Uint8Array, sensitivity: number): Uint32Array => {
    const bitmask = new Uint32Array(INDEX_LENGTH_U32)
    const ranged = (sensitivity / 300 + 0.66) * 100

    const s = Math.max(0, Math.min(100, ranged))

    const threshold = Math.floor((100 - s) * 2.55)

    for (let colorId = 0; colorId < anomalyFilter.length; colorId++) {
        if (anomalyFilter[colorId] < threshold) {
            const wordIndex = colorId >>> 5
            const bitPosition = colorId & 31
            bitmask[wordIndex] |= 1 << bitPosition
        }
    }
    return bitmask
}

export const drawAnomalyTimeline = (
    tiler: ColorFinderTiler,
    anomalyIndex: Uint8Array,
    start: number,
    end: number,
    canvas: HTMLCanvasElement,
    imageData: ImageData,
    sensitivity: number
) => {
    const context = canvas.getContext("2d")
    if (!context) return

    const ranged = (sensitivity / 300 + 0.66) * 100

    const s = Math.max(0, Math.min(100, ranged))

    const threshold = Math.floor((100 - s) * 2.55)
    const bitmask = createAnomalyMask(anomalyIndex, sensitivity)

    const { width, height } = imageData
    const pixelBuffer = new Uint32Array(imageData.data.buffer)
    const outArray = new Uint32Array(3)
    const centerY = height >> 1

    pixelBuffer.fill(0)

    for (let x = 0; x < width; x++) {
        tiler.convertPixelToTile(x, start, end, outArray)

        const [tileNum, zoom, tileIndex] = outArray
        const tile = tiler.getTileByHand(zoom, tileNum)
        if (!tile) continue

        const tileOffset = tileIndex * INDEX_LENGTH_U32

        let minScore = 255
        let hasMatch = false

        for (let wordIndex = 0; wordIndex < INDEX_LENGTH_U32; wordIndex++) {
            let overlap = tile[tileOffset + wordIndex] & bitmask[wordIndex]

            if (overlap !== 0) {
                hasMatch = true
                while (overlap !== 0) {
                    const lsb = overlap & -overlap
                    const bitIndex = 31 - Math.clz32(lsb)
                    const colorId = (wordIndex << 5) + bitIndex

                    const score = anomalyIndex[colorId]
                    if (score < minScore) {
                        minScore = score
                        if (minScore === 0) break
                    }
                    overlap &= ~lsb
                }
            }
            if (minScore === 0) break
        }

        if (hasMatch) {
            let alpha = 255
            if (threshold > 0) {
                // 1. Calculate linear strength (0.0 to 1.0)
                // 1.0 = Perfect Anomaly (Score 0)
                // 0.0 = Borderline (Score == Threshold)
                const ratio = Math.max(0, (threshold - minScore) / threshold)

                // 2. Apply Power Curve
                // This crushes the mid-tones. A ratio of 0.5 becomes 0.125.
                // Only strong signals survive.
                const curvedRatio = Math.pow(ratio, OPACITY_GAMMA)

                alpha = Math.floor(curvedRatio * 255)
            }

            // Only draw if it's visible enough to matter
            if (alpha > 5) {
                const finalColor = (alpha << 24) | ANOMALY_COLOR_BGR
                const topRowOffset = (centerY - 1) * width + x
                const bottomRowOffset = centerY * width + x
                pixelBuffer[topRowOffset] = finalColor
                pixelBuffer[bottomRowOffset] = finalColor
            }
        }
    }

    context.putImageData(imageData, 0, 0)
}
